import { reactive, toRefs, computed, watch } from '@vue/composition-api';
import { numberFormat } from '../helpers/utils';
import { ALLOCATION_BY } from '../enums/allocation';

const useSelectSkuCategory = (props, context, options = {}) => {
	const { defaultState } = options;

	const eventTab = reactive({
		tabs: [
			{
				value: ALLOCATION_BY.CATEGOTY,
				text: 'Category',
			},
			{
				value: ALLOCATION_BY.SKU,
				text: 'SKU',
			},
		],
		selectedTabValue: defaultState.selectedType || ALLOCATION_BY.CATEGOTY,
		isTabCategory: computed(() => eventTab.selectedTabValue === ALLOCATION_BY.CATEGOTY),
		isTabSku: computed(() => eventTab.selectedTabValue === ALLOCATION_BY.SKU),
		transformTabs: computed(() => {
			return eventTab.tabs.map((tab) => {
				return {
					...tab,
					color: eventTab.selectedTabValue === tab.value ? 'secondary' : 'light',
				};
			});
		}),
	});

	const eventCategory = reactive({
		selectedCategories: defaultState.selectedValues,
		selectedCategoryIds: eventTab.isTabCategory
			? defaultState.selectedValues.map(({ id }) => id)
			: [],
		// temporary skus info including: id, sku
		tempSkus: [],
		buttonCategoryText: computed(() => {
			const hasSelected = eventCategory.selectedCategories.length;
			return hasSelected
				? 'button.change_categories'
				: 'button.select_categories';
		}),
	});

	const eventSku = reactive({
		selectedSkus: eventTab.isTabSku
			? (defaultState.selectedValues || [])
			: [],
		selectedSkusAll: props.selectedCheckAll !== undefined
			? props.selectedCheckAll
			: false,
		selectedSkusCount: eventTab.isTabSku
			? ((defaultState.selectedValues && defaultState.selectedValues.length) || 0)
			: 0,
		selectedSkusCountFriendly: computed(() => numberFormat(eventSku.selectedSkusCount)),
		buttonSkuText: computed(() => {
			const hasSelected = eventSku.selectedSkus.length;
			return hasSelected
				? 'button.changeSkus'
				: 'button.selectSkus';
		}),
		limit: 22,
		limitSelectedSkus: computed(() => eventSku.selectedSkus.filter((item, index) => (index + 1) <= eventSku.limit)),
		isShowMore: computed(() => eventSku.selectedSkus.length > eventSku.limit),
	});

	const eventExcludeSku = reactive({
		selectedExcludeSkus: defaultState.selectedExcludeValues || [],
		selectedExcludeSkusAll: props.selectedCheckAll !== undefined
			? props.selectedCheckAll
			: false,
		selectedExcludeSkusCount: ((defaultState.selectedExcludeValues && defaultState.selectedExcludeValues.length) || 0),
		selectedExcludeSkusCountFriendly: computed(() => numberFormat(eventExcludeSku.selectedExcludeSkusCount)),
		buttonExcludeSkuText: computed(() => {
			const hasSelected = eventExcludeSku.selectedExcludeSkus.length;
			return hasSelected
				? 'button.changeSkus'
				: 'button.selectSkus';
		}),
	});

	const eventModal = reactive({
		modalCategoryName: 'modal-category',
		modalSkuName: 'modal-sku',
		currentModalName: computed(() => {
			if (eventTab.isTabCategory) {
				return eventModal.modalCategoryName;
			} else if (eventTab.isTabSku) {
				return eventModal.modalSkuName;
			}
			return null;
		}),
		buttonLabelText: computed(() => {
			if (eventTab.isTabCategory) {
				return 'message.categories';
			} else if (eventTab.isTabSku) {
				return 'message.skus';
			}
			return null;
		}),
		buttonOpenModalText: computed(() => {
			if (eventTab.isTabCategory) {
				return eventCategory.buttonCategoryText;
			} else if (eventTab.isTabSku) {
				return eventSku.buttonSkuText;
			}
			return null;
		}),
	});


	const handleUpdateCategories = (categories = []) => {
		eventCategory.selectedCategories = categories;
	};

	const handleCategorySubmit = (selectedCategories = []) => {
		eventCategory.selectedCategories = selectedCategories;
		eventCategory.selectedCategoryIds = selectedCategories.map(({ id }) => id);

		const values = (() => {
			if (eventTab.isTabCategory) {
				return eventCategory.selectedCategories;
			} else if (eventTab.isTabSku) {
				return eventSku.selectedSkus;
			}
			return [];
		})();
		context.emit('onDone', ALLOCATION_BY.CATEGOTY, values);
	};

	const handleSkuFetch = (data = []) => {
		data.forEach((item) => eventCategory.tempSkus.push(item));
	};

	const handleSkuSubmit = (isCheckAll = false, selectedSkusCount = 0, selectedSkuIds = []) => {
		if (eventSku.selectedSkus.length || selectedSkuIds.length) {
			eventSku.selectedSkus = selectedSkuIds;
		}

		eventSku.selectedSkusAll = isCheckAll;
		eventSku.selectedSkusCount = selectedSkusCount;

		context.emit('onDone', ALLOCATION_BY.SKU, selectedSkuIds, isCheckAll);
	};

	const handleExcludeSkuSubmit = (isCheckAll = false, selectedExcludeSkusCount = 0, selectedExcludeSkuIds = []) => {
		if (eventExcludeSku.selectedExcludeSkus.length || selectedExcludeSkuIds.length) {
			eventExcludeSku.selectedExcludeSkus = selectedExcludeSkuIds;
		}

		eventExcludeSku.selectedExcludeSkusAll = isCheckAll;
		eventExcludeSku.selectedExcludeSkusCount = selectedExcludeSkusCount;

		context.emit('onDoneExcludeSku', selectedExcludeSkuIds, isCheckAll);
	};

	const onClickTab = (event, tabValue) => {
		eventTab.selectedTabValue = tabValue;
	};

	const handleSelectedTabValue = (newTabValue) => {
		context.emit('update:type', newTabValue);
	};

	const handlePropType = (newType) => {
		eventTab.selectedTabValue = newType;
	};

	const handlePropValues = (newValues) => {
		if (eventTab.isTabCategory) {
			eventCategory.selectedCategoryIds = newValues;
		} else if (eventTab.isTabSku) {
			eventSku.selectedSkus = newValues;
			eventSku.selectedSkusCount = newValues.length;
		}
	};

	const handlePropExcludeValues = (newValues) => {
		eventExcludeSku.selectedExcludeSkus = newValues;
		eventExcludeSku.selectedExcludeSkusCount = newValues.length;
	};

	const handlePropCheckAll = (newCheckAll) => {
		eventSku.selectedSkusAll = newCheckAll;
	};

	// watch `selectedTabValue` was change
	watch(() => eventTab.selectedTabValue, handleSelectedTabValue);

	// watch `prop type` was changed
	watch(() => props.type, handlePropType);

	// watch `prop values` was changed
	watch(() => props.values, handlePropValues);

	// watch `prop values` was changed
	watch(() => props.excludeValues, handlePropExcludeValues);

	// watch `prop checkAll` was changed
	watch(() => props.checkAll, handlePropCheckAll);

	return {
		...toRefs(eventTab),
		...toRefs(eventCategory),
		...toRefs(eventSku),
		...toRefs(eventExcludeSku),
		...toRefs(eventModal),
		handleUpdateCategories,
		handleCategorySubmit,
		handleSkuFetch,
		handleSkuSubmit,
		handleExcludeSkuSubmit,
		onClickTab,
	};
};

export default useSelectSkuCategory;
